import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/joy';
import { observer } from 'mobx-react-lite';

import campaignStore from '../../stores/CampaignStore';
import organizationStore from '../../stores/OrganizationStore';

interface RecentCampaignHighlightsProps {
  limit?: number;
}

const RecentCampaignHighlights = observer(({ limit = 3 }: RecentCampaignHighlightsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedOrg } = organizationStore;
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCampaigns = async () => {
      if (selectedOrg) {
        setLoading(true);
        await campaignStore.fetchCampaigns(selectedOrg, t);
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [selectedOrg, t]);

  // Get campaigns from the store
  const { campaigns, loading: storeLoading } = campaignStore;

  // Get the most recent campaigns, limited by the limit prop
  const recentCampaigns = [...campaigns]
    .sort((a, b) => new Date(b.start_time || '').getTime() - new Date(a.start_time || '').getTime())
    .slice(0, limit);

  // Helper function to get status chip
  const getStatusChip = (status: number) => {
    if (status === 1) {
      // Active
      return (
        <Chip color='success' size='sm' startDecorator={<CheckCircleIcon fontSize='small' />} variant='soft'>
          {t('dashboard.active', 'Active')}
        </Chip>
      );
    }

    // Inactive
    return (
      <Chip color='neutral' size='sm' variant='soft'>
        {t('dashboard.inactive', 'Inactive')}
      </Chip>
    );
  };

  return (
    <Card variant='outlined'>
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography level='title-md'>{t('dashboard.recent_campaigns', 'Recent Campaign Highlights')}</Typography>
        <Button color='primary' onClick={() => navigate('/campaigns')} size='sm' variant='plain'>
          {t('dashboard.view_all', 'View All')}
        </Button>
      </Box>
      <CardContent>
        {/* Loading state */}
        {(loading || storeLoading) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size='sm' />
          </Box>
        )}

        {/* Empty state */}
        {!loading && !storeLoading && recentCampaigns.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color='neutral' level='body-sm'>
              {t('dashboard.no_campaigns', 'No campaigns found')}
            </Typography>
          </Box>
        )}

        {/* Campaign list */}
        {!loading && !storeLoading && recentCampaigns.length > 0 && (
          <Stack divider={<Divider />} spacing={2}>
            {recentCampaigns.map((campaign) => (
              <Box key={campaign.id}>
                <Stack direction='row' spacing={2}>
                  <Box
                    sx={{
                      width: 80,
                      height: 60,
                      bgcolor: 'neutral.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 'sm',
                      overflow: 'hidden'
                    }}
                  >
                    <Typography
                      level='body-sm'
                      sx={{
                        color: 'neutral.500',
                        textAlign: 'center',
                        fontSize: '0.75rem',
                        lineHeight: 1.2
                      }}
                    >
                      {t('dashboard.campaign', 'Campaign')}
                    </Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Stack alignItems='flex-start' direction='row' justifyContent='space-between'>
                      <Typography level='title-sm'>{campaign.name}</Typography>
                      {getStatusChip(campaign.status)}
                    </Stack>
                    <Typography color='neutral' level='body-xs'>
                      {new Date(campaign.start_time || '').toLocaleDateString()}
                    </Typography>
                    <Stack direction='row' mt={1} spacing={2}>
                      <Typography level='body-xs'>
                        <strong>{t('dashboard.description', 'Description')}:</strong>{' '}
                        {campaign.description.substring(0, 50)}
                        {campaign.description.length > 50 ? '...' : ''}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
});

export default RecentCampaignHighlights;
